import {
  PaginatedResponse,
  Account,
  Folder,
  Entry,
  PaginatedSearchResponse,
  AllSearchResults,
  AccountSearchResults,
  Classification
} from '@/types/cit-public-api';

import { ENTRIES_PER_PAGE } from './constants';
import HTTPError from './errors/http-error';
import NotFoundError from './errors/notfound-error';

// TODO: change permenantly to public api address when made public
const API_URL = process.env.NEXT_PUBLIC_PUBLIC_API_BASE_URL;

export const api = {
  // Get single entry details
  getEntryDetails: async (
    accountId: string,
    entryId: string,
    citOrigin: string,
    folderId?: string
  ): Promise<Entry> => {
    const url = `${API_URL}/accounts/${accountId}/${
      folderId ? `folders/${folderId}/` : ''
    }entries/${entryId}?pn=0`;

    const response = await fetch(url, {
      headers: {
        'x-cit-origin': citOrigin
      }
    });

    if (response.status === 404) {
      console.error('API ERROR: getEntryDetails', response.statusText, url);
      throw new NotFoundError(`Entry not found: ${entryId}`);
    }

    if (!response.ok) {
      console.error('API ERROR: getEntryDetails', response.statusText, url);
      throw new HTTPError(response.status, `Failed to fetch entry details: ${response.statusText}`);
    }

    return await response.json();
  },

  // Get single entry details
  getAccountEntries: async (
    accountId: string,
    size = ENTRIES_PER_PAGE,
    citOrigin: string,
    cursor?: string | undefined
  ): Promise<PaginatedResponse<Entry>> => {
    const params = new URLSearchParams();
    params.append('size', size.toString());
    if (cursor) {
      params.append('cursor', cursor);
    }

    const url = `${API_URL}/accounts/${accountId}/entries?${params}`;
    const response = await fetch(url, {
      headers: {
        'x-cit-origin': citOrigin
      }
    });

    if (response.status === 404) {
      console.error('API ERROR: getAccountEntries', response.statusText, url);
      throw new NotFoundError(`Account entries not found: ${accountId}`);
    }

    if (!response.ok) {
      console.error('API ERROR: getAccountEntries', response.statusText, url);
      throw new HTTPError(
        response.status,
        `Failed to fetch account entries: ${response.statusText}`
      );
    }
    return response.json();
  },

  // Get search results within an account
  getAccountSearchResults: async (
    query: string,
    accountId: string,
    citOrigin: string
  ): Promise<AccountSearchResults> => {
    const url = `${API_URL}/accounts/${accountId}/search?query=${encodeURIComponent(query)}`;
    const response = await fetch(url, {
      headers: {
        'x-cit-origin': citOrigin
      }
    });
    if (response.status === 404) {
      console.error('API ERROR: getAccountSearchResults', response.statusText, url);
      throw new NotFoundError(`Account search results not found: ${accountId}`);
    }

    if (!response.ok) {
      console.error('API ERROR: getAccountSearchResults', response.statusText, url);
      throw new HTTPError(
        response.status,
        `Failed to fetch account search results: ${response.statusText}`
      );
    }

    const data = await response.json();
    return data;
  },

  // Get search results across all accounts
  getAllSearchResults: async (query: string, citOrigin: string): Promise<AllSearchResults> => {
    const url = `${API_URL}/search?query=${encodeURIComponent(query)}`;
    const response = await fetch(url, {
      headers: {
        'x-cit-origin': citOrigin
      }
    });

    if (response.status === 404) {
      console.error('API ERROR: getAllSearchResults', response.statusText, url);
      throw new NotFoundError(`All search results not found: ${query}`);
    }

    if (!response.ok) {
      console.error('API ERROR: getAllSearchResults', response.statusText, url);
      throw new HTTPError(
        response.status,
        `Failed to fetch all search results: ${response.statusText}`
      );
    }

    const data = await response.json();
    return data;
  },

  // Get folder details raw
  getFolderDetails: async (
    accountId: string,
    citOrigin: string,
    folderId?: string | undefined
  ): Promise<Folder> => {
    const url = `${API_URL}/accounts/${accountId}/folders${folderId ? `/${folderId}` : ''}`;
    const response = await fetch(url, {
      headers: {
        'x-cit-origin': citOrigin
      }
    });

    if (response.status === 404) {
      console.error('API ERROR: getFolderDetails', response.statusText, url);
      throw new NotFoundError(`Folder details not found: ${folderId}`);
    }

    if (!response.ok) {
      console.error('API ERROR: getFolderDetails', response.statusText, url);
      throw new HTTPError(
        response.status,
        `Failed to fetch folder details: ${response.statusText}`
      );
    }

    return response.json();
  },

  // Get featured items
  getFeatured: async (citOrigin: string) => {
    const url = `${API_URL}/featured`;
    const response = await fetch(url, {
      headers: {
        'x-cit-origin': citOrigin
      }
    });

    if (!response.ok) {
      console.error('API ERROR: getFeatured', response.statusText, url);
      throw new HTTPError(
        response.status,
        `Failed to fetch featured items: ${response.statusText}`
      );
    }
    return response.json() as Promise<{
      featured_accounts: Account[];
      featured_classifications: Classification[];
    }>;
  },

  getClassifications: async (citOrigin: string): Promise<Classification[]> => {
    const url = `${API_URL}/classifications`;
    const response = await fetch(url, {
      headers: {
        'x-cit-origin': citOrigin
      }
    });

    if (!response.ok) {
      console.error('API ERROR: getClassifications', response.statusText, url);
      throw new HTTPError(
        response.status,
        `Failed to fetch classifications: ${response.statusText}`
      );
    }
    return response.json();
  },

  // Get classification entries
  getClassificationEntries: async (
    classificationId: string,
    size = ENTRIES_PER_PAGE,
    citOrigin: string,
    cursor?: string | undefined
  ): Promise<PaginatedResponse<Entry>> => {
    const params = new URLSearchParams();
    params.append('size', size.toString());
    if (cursor) {
      params.append('cursor', cursor);
    }

    const url = `${API_URL}/classifications/${classificationId}/entries?${params}`;
    const response = await fetch(url, {
      headers: {
        'x-cit-origin': citOrigin
      }
    });

    if (response.status === 404) {
      console.error('API ERROR: getClassificationEntries', response.statusText, url);
      throw new NotFoundError(`Classification entries not found: ${classificationId}`);
    }

    if (!response.ok) {
      console.error('API ERROR: getClassificationEntries', response.statusText, url);
      throw new HTTPError(
        response.status,
        `Failed to fetch classification entries: ${response.statusText}`
      );
    }
    return response.json();
  },

  getAllAccounts: async (
    citOrigin: string,
    size?: number,
    cursor?: string | undefined
  ): Promise<PaginatedResponse<Account>> => {
    const params = new URLSearchParams();
    if (size) {
      params.append('size', size.toString());
    }
    if (cursor) {
      params.append('cursor', cursor);
    }

    const url = `${API_URL}/accounts${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await fetch(url, {
      headers: {
        'x-cit-origin': citOrigin
      }
    });

    if (!response.ok) {
      console.error('API ERROR: getAllAccounts', response.statusText, url);
      throw new HTTPError(response.status, `Failed to fetch accounts: ${response.statusText}`);
    }
    return response.json();
  },

  getAccountDetails: async (accountId: string, citOrigin: string): Promise<Account> => {
    const url = `${API_URL}/accounts/${accountId}`;
    const response = await fetch(url, {
      headers: {
        'x-cit-origin': citOrigin
      }
    });

    if (response.status === 404) {
      console.error('API ERROR: getAccountDetails', response.statusText, url);
      throw new NotFoundError(`Account details not found: ${accountId}`);
    }

    if (!response.ok) {
      console.error('API ERROR: getAccountDetails', response.statusText, url);
      throw new HTTPError(
        response.status,
        `Failed to fetch account details: ${response.statusText}`
      );
    }

    const data: Account = await response.json();

    // if user did not provide an account name, set it to a default value of 'anonymous...'
    if (data.name === undefined) {
      data.name = 'anonymous...';
    }

    return data;
  },

  getAccountFolders: async (accountId: string, citOrigin: string): Promise<Folder[]> => {
    const url = `${API_URL}/accounts/${accountId}/folders`;

    const response = await fetch(url, {
      headers: {
        'x-cit-origin': citOrigin
      }
    });

    if (response.status === 404) {
      console.error('API ERROR: getAccountFolders', response.statusText, url);
      throw new NotFoundError(`Account folders not found: ${accountId}`);
    }

    if (!response.ok) {
      console.error('API ERROR: getAccountFolders', response.statusText, url);
      throw new HTTPError(response.status, `Failed to fetch folders: ${response.statusText}`);
    }
    return response.json();
  },

  // Get entries by classification paginated
  getEntriesByClassificationPaginated: async (
    classificationId: string,
    size: number,
    citOrigin: string,
    searchQuery?: string,
    cursor?: string | undefined
  ): Promise<PaginatedResponse<Entry> | PaginatedSearchResponse | undefined> => {
    const params = new URLSearchParams();

    if (cursor) {
      params.append('cursor', cursor);
    }
    if (searchQuery) {
      params.append('query', searchQuery);
    }
    params.append('size', size.toString());

    const url = `${API_URL}/classifications/${classificationId}/${
      searchQuery ? 'search' : 'entries'
    }?${params.toString()}`;

    const response = await fetch(url, {
      headers: {
        'x-cit-origin': citOrigin
      }
    });

    if (!response.ok) {
      console.error('API ERROR: getEntriesByClassificationPaginated', response.statusText, url);
      throw new HTTPError(response.status, `Failed to fetch entries by classification`);
    }
    const responseData = (await response.json()) as
      | PaginatedResponse<Entry>
      | PaginatedSearchResponse
      | undefined;

    return responseData;
  },

  // Get entries by folder paginated
  getEntriesByFolderPaginated: async (
    accountId: string,
    folderId: string,
    size: number,
    citOrigin: string,
    searchQuery?: string,
    cursor?: string | undefined
  ): Promise<PaginatedResponse<Entry> | PaginatedSearchResponse | undefined> => {
    const params = new URLSearchParams();

    if (cursor) {
      params.append('cursor', cursor);
    }
    if (searchQuery) {
      params.append('query', searchQuery);
    }
    params.append('size', size.toString());

    const url = `${API_URL}/accounts/${accountId}/folders/${folderId}/${
      searchQuery ? 'search' : 'entries'
    }?${params.toString()}`;

    const response = await fetch(url, {
      headers: {
        'x-cit-origin': citOrigin
      }
    });

    if (response.status === 404) {
      console.error('API ERROR: getEntriesByFolderPaginated', response.statusText, url);
      throw new NotFoundError(`Entries by folder not found: ${folderId}`);
    }

    if (!response.ok) {
      console.error('API ERROR: getEntriesByFolderPaginated', response.statusText, url);
      throw new HTTPError(response.status, `Failed to fetch entries by folder`);
    }
    const responseData = (await response.json()) as
      | PaginatedResponse<Entry>
      | PaginatedSearchResponse;

    return responseData;
  },

  // Get all entries by account paginated
  getEntriesByAccountPaginated: async (
    accountId: string,
    size: number,
    citOrigin: string,
    searchQuery?: string,
    cursor?: string | undefined
  ): Promise<PaginatedResponse<Entry> | PaginatedSearchResponse | undefined> => {
    const params = new URLSearchParams();

    if (cursor) {
      params.append('cursor', cursor);
    }
    if (searchQuery) {
      params.append('query', searchQuery);
    }
    params.append('size', size.toString());

    const url = `${API_URL}/accounts/${accountId}/entries${
      searchQuery ? '/search' : ''
    }?${params.toString()}`;

    const response = await fetch(url, {
      headers: {
        'x-cit-origin': citOrigin
      }
    });

    if (response.status === 404) {
      console.error('API ERROR: getEntriesByAccountPaginated', response.statusText, url);
      throw new NotFoundError(`Entries by account not found: ${accountId}`);
    }

    if (!response.ok) {
      console.error('API ERROR: getEntriesByAccountPaginated', response.statusText, url);
      throw new HTTPError(
        response.status,
        `Failed to fetch entries by account: ${response.statusText}`
      );
    }
    const responseData = (await response.json()) as
      | PaginatedResponse<Entry>
      | PaginatedSearchResponse;

    return responseData;
  }
};
